// src/components/shopping/JewelleryProductDetailModal.tsx
import React, { useState, useEffect } from 'react';
import {
  X, Heart, Share2, MessageSquare, ShoppingBag, MapPin,
  Clock, Tag, Shield, User, Loader2, CheckCircle, Send,
  ChevronLeft, ChevronRight, AlertTriangle,
  Link as LinkIcon, Users, ArrowLeft, CreditCard,
  Gem, Diamond, Award, Crown, Medal, Sparkles,
  Stamp, Ruler, Weight, Scale, Hash, Palette,
  Gavel, Timer, DollarSign, TrendingUp, Eye,
  Info, FileCheck, BadgeCheck, Star, StarHalf
} from 'lucide-react';
import { db } from '../../services/firebase';
import {
  doc, setDoc, addDoc, collection, increment, serverTimestamp,
  getDoc, updateDoc
} from 'firebase/firestore';
import { useAuth } from '../../hooks/useAuth';

interface JewelleryProductDetailModalProps {
  item: any;
  isOpen: boolean;
  onClose: () => void;
  onToggleWishlist?: (id: string, type: 'product') => void;
  isWishlisted: boolean;
  userProfile?: any;
  currentUser?: any;
  requireAuth?: (fn: () => void, message: string) => void;
  onViewProfile?: (userId: string) => void;
  onSendMessage?: (sellerId: string, sellerName: string) => void;
  onAddToCart?: (item: any) => void;
  onBuyNow?: (item: any) => void;
  cartItems?: any[];
}

/* =========================================================================
   SCOOP & STONE DESIGN TOKENS — copied 1:1 from the ScoopStoneApp.jsx
   CSS custom properties, so this modal reads as the same product.
   ========================================================================= */
const INK = '#17171A';
const MUTED = '#6B6B63';
const GOLD = '#A6812C';
const BURGUNDY = '#7A1F2B';
const BG = '#FDFCFA';
const SURFACE = '#FFFFFF';
const SURFACE_ALT = '#FAF8F3';
const LINE = 'rgba(23,23,26,0.12)';
const SERIF = "'Cormorant Garamond', serif";
const MONO = "'JetBrains Mono', monospace";
const SANS = "'Inter', sans-serif";

// radii, matched exactly: buttons=2px, panels/cards=4px, pills/badges=999px
const R_BTN = '2px';
const R_PANEL = '4px';
const R_PILL = '999px';

const FONT_IMPORT = "@import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,400;0,500;0,600;1,400&family=Inter:wght@400;500;600&family=JetBrains+Mono:wght@400;500&display=swap');";

// 🟢 WAX SEAL BADGE — unchanged, this is the anchor the whole palette is built from
const WaxSeal = ({ size = 40, className = "" }) => (
  <div className={`relative ${className}`} style={{ width: size, height: size }}>
    <svg width={size} height={size} viewBox="0 0 100 100">
      <circle cx="50" cy="50" r="46" fill="#7A1F2B" />
      <circle cx="50" cy="50" r="46" fill="none" stroke="#A6812C" strokeWidth="2" />
      <circle cx="50" cy="50" r="38" fill="none" stroke="#A6812C" strokeWidth="1" opacity="0.6" />
      <text x="50" y="46" textAnchor="middle" fill="#F4E9C9" fontSize="11" fontFamily="serif" letterSpacing="1">
        SCOOP
      </text>
      <text x="50" y="60" textAnchor="middle" fill="#F4E9C9" fontSize="8" fontFamily="serif" letterSpacing="2">
        VERIFIED
      </text>
    </svg>
  </div>
);

// 🟢 "Eyebrow" label — same treatment as .eyebrow in ScoopStoneApp
// (JetBrains Mono, 11px, uppercase, tracked, burgundy) — used for every
// small section label instead of the old white/40 micro-caps.
const Eyebrow = ({ children }: { children: React.ReactNode }) => (
  <div
    style={{
      fontFamily: MONO,
      fontSize: 11,
      letterSpacing: '1px',
      textTransform: 'uppercase',
      color: BURGUNDY,
      marginBottom: 10,
    }}
  >
    {children}
  </div>
);

// 🟢 Condition tag — same as ScoopStoneApp's <ConditionTag>: plain mono
// burgundy text, no pill/background, not a "badge"
const ConditionTag = ({ text }: { text: string }) => (
  <span style={{ fontFamily: MONO, fontSize: 11, color: BURGUNDY, whiteSpace: 'nowrap' }}>{text}</span>
);

// 🟢 Primary / outline buttons — 2px radius, 14px normal-case type,
// exactly matching .btn-primary / .btn-outline
const PrimaryButton = ({ children, style, disabled, ...rest }: any) => (
  <button
    {...rest}
    disabled={disabled}
    style={{
      background: disabled ? SURFACE_ALT : INK,
      color: disabled ? MUTED : '#FDFCFA',
      border: 'none',
      padding: '13px 22px',
      fontSize: 14,
      fontFamily: SANS,
      borderRadius: R_BTN,
      cursor: disabled ? 'default' : 'pointer',
      transition: 'background 0.2s',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 8,
      ...style,
    }}
    onMouseEnter={(e: any) => { if (!disabled) e.currentTarget.style.background = GOLD; }}
    onMouseLeave={(e: any) => { if (!disabled) e.currentTarget.style.background = INK; }}
  >
    {children}
  </button>
);

const OutlineButton = ({ children, style, colorOverride, ...rest }: any) => (
  <button
    {...rest}
    style={{
      border: `1px solid ${colorOverride || INK}`,
      background: 'transparent',
      color: colorOverride || INK,
      padding: '13px 22px',
      fontSize: 14,
      fontFamily: SANS,
      borderRadius: R_BTN,
      cursor: 'pointer',
      transition: 'all 0.2s',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 8,
      ...style,
    }}
    onMouseEnter={(e: any) => { e.currentTarget.style.background = colorOverride || INK; e.currentTarget.style.color = '#FDFCFA'; }}
    onMouseLeave={(e: any) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = colorOverride || INK; }}
  >
    {children}
  </button>
);

// 🟢 AUCTION TIMER — same visual states as .auction-timer (pill, mono,
// turns burgundy under an hour) instead of plain colored text
const AuctionTimer = ({ endsAt }: { endsAt: number }) => {
  const [timeLeft, setTimeLeft] = useState(endsAt - Date.now());

  useEffect(() => {
    const interval = setInterval(() => setTimeLeft(endsAt - Date.now()), 1000);
    return () => clearInterval(interval);
  }, [endsAt]);

  const ended = timeLeft <= 0;
  const ending = timeLeft < 1000 * 60 * 60;
  const hours = Math.floor(timeLeft / (1000 * 60 * 60));
  const minutes = Math.floor((timeLeft % (1000 * 60 * 60)) / (1000 * 60));
  const seconds = Math.floor((timeLeft % (1000 * 60)) / 1000);

  let label = 'Auction Ended';
  if (!ended) {
    if (hours > 24) label = `${Math.floor(hours / 24)}d ${hours % 24}h left`;
    else if (hours > 0) label = `${hours}h ${minutes}m left`;
    else label = `${minutes}m ${seconds}s left`;
  }

  return (
    <span
      className={ending && !ended ? 'animate-pulse' : ''}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 6,
        fontFamily: MONO, fontSize: 12.5,
        background: ending || ended ? '#FCEFEF' : SURFACE_ALT,
        border: `1px solid ${ending || ended ? BURGUNDY : LINE}`,
        borderRadius: R_PILL,
        padding: '8px 14px',
        color: ending || ended ? BURGUNDY : INK,
        whiteSpace: 'nowrap',
      }}
    >
      <Gavel size={13} />
      {label}
    </span>
  );
};

export const JewelleryProductDetailModal: React.FC<JewelleryProductDetailModalProps> = ({
  item,
  isOpen,
  onClose,
  onToggleWishlist,
  isWishlisted,
  userProfile,
  currentUser,
  requireAuth,
  onViewProfile,
  onSendMessage,
  onAddToCart,
  onBuyNow,
  cartItems = []
}) => {
  const [isMakingOffer, setIsMakingOffer] = useState(false);
  const [offerAmount, setOfferAmount] = useState('');
  const [isSendingAction, setIsSendingAction] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [showShareMenu, setShowShareMenu] = useState(false);
  const [quickMessage, setQuickMessage] = useState('Hi, is this still available?');
  const [showSuccess, setShowSuccess] = useState(false);
  const [isPlacingBid, setIsPlacingBid] = useState(false);
  const [bidAmount, setBidAmount] = useState('');
  const [tab, setTab] = useState<'details' | 'description' | 'jeweller'>('details');

  const jewelleryImages = item?.images?.length > 0 ? item.images : [item?.image];
  const sellerName = item?.userName ||
    item?.sellerName ||
    item?.brandName ||
    item?.businessName ||
    item?.displayName ||
    item?.storeName ||
    'Unknown Jeweller';
  const sellerId = item?.userId || item?.sellerId || item?.businessId || item?.storeId || item?.workspaceId;
  const isSold = item?.status === 'sold' || item?.stock === 0;
  const isInCart = cartItems.some(cartItem => cartItem.id === item?.id);
  const isAuction = item?.isAuction || false;
  const isAuctionActive = isAuction && item?.auctionStatus === 'active';
  const displayPrice = isAuction ? (item?.currentBid || item?.startingBid || item?.price) : item?.price;

  // 🟢 BID INCREMENT
  const bidIncrement = item?.bidIncrement || 100;
  const nextBidAmount = (displayPrice || 0) + bidIncrement;

  // 🟢 SPECIFICATIONS — rendered as a real two-column table (.spec-table),
  // not a card grid, matching the product detail page exactly
  const specs: [string, string][] = [
    ['Metal Type', item?.metalType],
    ['Hallmark', item?.hallmark],
    ['Material', item?.material],
    ['Gemstone', item?.gemstone],
    ['Carat Weight', item?.caratWeight ? `${item.caratWeight}ct` : null],
    ['Total Carat Weight', item?.totalCaratWeight ? `${item.totalCaratWeight}ct` : null],
    ['Gemstone Clarity', item?.gemstoneClarity],
    ['Gemstone Colour', item?.gemstoneColour],
    ['Clarity', item?.clarity],
    ['Colour Grade', item?.colourGrade],
    ['Cut', item?.cut],
    ['Ring Size', item?.ringSize],
    ['Chain Length', item?.chainLength],
    ['Bracelet Length', item?.braceletLength],
    ['Clasp', item?.clasp],
    ['Origin', item?.origin],
    ['Setting', item?.setting],
    ['Weight', item?.weight ? `${item.weight}${item.weightUnit || 'g'}` : null],
  ].filter(([, v]) => v) as [string, string][];

  useEffect(() => {
    if (item && isOpen) {
      setCurrentImageIndex(0);
      setIsMakingOffer(false);
      setOfferAmount('');
      setBidAmount('');
      setIsPlacingBid(false);
      setTab('details');
    }
  }, [item, isOpen]);

  if (!isOpen || !item) return null;

  const handleAddToCart = () => {
    if (!currentUser) {
      requireAuth?.(() => {}, "Log in to add items to your cart.");
      return;
    }

    if (isSold) {
      alert("This item has been sold.");
      return;
    }

    const cartItem = {
      id: item.id,
      name: item.itemName || item.name,
      price: isAuction ? (item.currentBid || item.startingBid || item.price) : item.price,
      image: item.image,
      images: item.images || [],
      quantity: 1,
      sellerId: sellerId,
      sellerName: sellerName,
      sellerAvatar: item.userAvatar || item.sellerAvatar || '',
      workspaceType: 'jewellery',
      type: 'jewellery',
      workspaceId: sellerId,
      businessId: sellerId,
      condition: item.condition || '',
      size: item.size || '',
      color: item.color || '',
      authenticationBadge: item.authenticationBadge || false,
      isOneOfOne: true,
      hallmark: item.hallmark || '',
      metalType: item.metalType || '',
      gemstone: item.gemstone || '',
      originalItem: item
    };

    onAddToCart?.(cartItem);
  };

  const handleBuyNow = () => {
    if (!currentUser) {
      requireAuth?.(() => {}, "Log in to purchase this item.");
      return;
    }

    if (isSold) {
      alert("This item has been sold.");
      return;
    }

    const cartItem = {
      id: item.id,
      name: item.itemName || item.name,
      price: isAuction ? (item.currentBid || item.startingBid || item.price) : item.price,
      image: item.image,
      images: item.images || [],
      quantity: 1,
      sellerId: sellerId,
      sellerName: sellerName,
      sellerAvatar: item.userAvatar || item.sellerAvatar || '',
      workspaceType: 'jewellery',
      type: 'jewellery',
      workspaceId: sellerId,
      businessId: sellerId,
      condition: item.condition || '',
      size: item.size || '',
      color: item.color || '',
      authenticationBadge: item.authenticationBadge || false,
      isOneOfOne: true,
      hallmark: item.hallmark || '',
      metalType: item.metalType || '',
      gemstone: item.gemstone || '',
      originalItem: item
    };

    onBuyNow?.(cartItem);
  };

  const handleCopyLink = () => {
    const shareUrl = `${window.location.origin}/jewellery?post=${item.id}`;
    navigator.clipboard.writeText(shareUrl);
    setShowShareMenu(false);
    alert("Link copied to clipboard!");
  };

  const handleNativeShare = async () => {
    const shareUrl = `${window.location.origin}/jewellery?post=${item.id}`;
    if (navigator.share) {
      try {
        await navigator.share({
          title: item.itemName || item.name,
          text: `Discover "${item.itemName || item.name}" on Scoop Hype! ✨`,
          url: shareUrl
        });
        setShowShareMenu(false);
      } catch (err) {
        console.error("Share failed:", err);
      }
    } else {
      handleCopyLink();
    }
  };

  const handleViewProfile = () => {
    if (sellerId && onViewProfile) {
      onClose();
      setTimeout(() => {
        onViewProfile(sellerId);
      }, 300);
    } else {
      alert("This jeweller's profile could not be found.");
    }
  };

  const handleSendMessage = () => {
    if (!currentUser) {
      requireAuth?.(() => {}, "Log in to message this jeweller.");
      return;
    }

    if (onSendMessage && sellerId) {
      onSendMessage(sellerId, sellerName);
    } else {
      window.dispatchEvent(new CustomEvent('open-messages', {
        detail: { userId: sellerId }
      }));
    }
  };

  const handleQuickAsk = () => {
    if (!currentUser) {
      requireAuth?.(() => {}, "Log in to send a message.");
      return;
    }

    if (!sellerId) {
      alert("Seller ID not found.");
      return;
    }

    setIsSendingAction(true);

    try {
      const chatId = [currentUser.uid, sellerId].sort().join('_');
      const chatRef = doc(db, 'chats', chatId);

      setDoc(chatRef, {
        participants: [currentUser.uid, sellerId],
        updatedAt: serverTimestamp(),
        participantNames: {
          [currentUser.uid]: userProfile?.displayName || currentUser.displayName || 'User',
          [sellerId]: sellerName
        },
        participantAvatars: {
          [currentUser.uid]: userProfile?.photoURL || '',
          [sellerId]: item?.userAvatar || item?.sellerAvatar || ''
        },
        hidden: { [currentUser.uid]: false, [sellerId]: false },
        lastMessage: quickMessage || 'Hi, is this still available?',
        [`unreadCount.${sellerId}`]: increment(1)
      }, { merge: true });

      addDoc(collection(db, 'chats', chatId, 'messages'), {
        text: quickMessage || 'Hi, is this still available?',
        senderId: currentUser.uid,
        createdAt: serverTimestamp(),
        messageType: 'text',
        productName: item.itemName || item.name,
        productId: item.id,
        productImage: item.image
      });

      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 2000);
    } catch (error) {
      console.error("Error sending message:", error);
      alert("Failed to send message. Please try again.");
    } finally {
      setIsSendingAction(false);
    }
  };

  const handleMakeOffer = () => {
    if (!currentUser) {
      requireAuth?.(() => {}, "Log in to make an offer.");
      return;
    }

    if (!offerAmount || isNaN(Number(offerAmount))) {
      alert("Please enter a valid offer amount.");
      return;
    }

    setIsSendingAction(true);

    try {
      const chatId = [currentUser.uid, sellerId].sort().join('_');
      const chatRef = doc(db, 'chats', chatId);

      const message = `${userProfile?.displayName || 'A user'} made an offer of R${offerAmount} for "${item.itemName || item.name}".`;

      setDoc(chatRef, {
        participants: [currentUser.uid, sellerId],
        updatedAt: serverTimestamp(),
        participantNames: {
          [currentUser.uid]: userProfile?.displayName || currentUser.displayName || 'User',
          [sellerId]: sellerName
        },
        participantAvatars: {
          [currentUser.uid]: userProfile?.photoURL || '',
          [sellerId]: item?.userAvatar || item?.sellerAvatar || ''
        },
        hidden: { [currentUser.uid]: false, [sellerId]: false },
        lastMessage: message,
        [`unreadCount.${sellerId}`]: increment(1)
      }, { merge: true });

      addDoc(collection(db, 'chats', chatId, 'messages'), {
        text: message,
        senderId: currentUser.uid,
        createdAt: serverTimestamp(),
        messageType: 'offer',
        offerAmount: Number(offerAmount),
        productName: item.itemName || item.name,
        productId: item.id,
        productImage: item.image,
        offerStatus: 'pending'
      });

      setShowSuccess(true);
      setTimeout(() => {
        setShowSuccess(false);
        setIsMakingOffer(false);
        setOfferAmount('');
      }, 2000);
    } catch (error) {
      console.error("Error sending offer:", error);
      alert("Failed to send offer. Please try again.");
    } finally {
      setIsSendingAction(false);
    }
  };

  const handlePlaceBid = async () => {
    if (!currentUser) {
      requireAuth?.(() => {}, "Log in to place a bid.");
      return;
    }

    if (!bidAmount || isNaN(Number(bidAmount))) {
      alert("Please enter a valid bid amount.");
      return;
    }

    const bidValue = Number(bidAmount);
    const currentBid = displayPrice || 0;
    const minBid = currentBid + bidIncrement;

    if (bidValue < minBid) {
      alert(`Minimum bid is R${minBid.toFixed(2)}`);
      return;
    }

    setIsPlacingBid(true);

    try {
      // Update the product with the new bid
      const productRef = doc(db, 'products', item.id);
      await updateDoc(productRef, {
        currentBid: bidValue,
        numberOfBids: increment(1),
        highestBidderId: currentUser.uid,
        updatedAt: serverTimestamp()
      });

      // Send notification to seller
      const chatId = [currentUser.uid, sellerId].sort().join('_');
      const chatRef = doc(db, 'chats', chatId);

      const message = `${userProfile?.displayName || 'A user'} placed a bid of R${bidValue.toFixed(2)} on "${item.itemName || item.name}".`;

      setDoc(chatRef, {
        participants: [currentUser.uid, sellerId],
        updatedAt: serverTimestamp(),
        participantNames: {
          [currentUser.uid]: userProfile?.displayName || currentUser.displayName || 'User',
          [sellerId]: sellerName
        },
        participantAvatars: {
          [currentUser.uid]: userProfile?.photoURL || '',
          [sellerId]: item?.userAvatar || item?.sellerAvatar || ''
        },
        hidden: { [currentUser.uid]: false, [sellerId]: false },
        lastMessage: message,
        [`unreadCount.${sellerId}`]: increment(1)
      }, { merge: true });

      addDoc(collection(db, 'chats', chatId, 'messages'), {
        text: message,
        senderId: currentUser.uid,
        createdAt: serverTimestamp(),
        messageType: 'bid',
        bidAmount: bidValue,
        productName: item.itemName || item.name,
        productId: item.id,
        productImage: item.image,
      });

      alert(`✅ Bid of R${bidValue.toFixed(2)} placed successfully!`);
      setBidAmount('');
      setIsPlacingBid(false);

      // Refresh the item data
      const updatedDoc = await getDoc(productRef);
      if (updatedDoc.exists()) {
        const data = updatedDoc.data();
        item.currentBid = data.currentBid;
        item.numberOfBids = data.numberOfBids;
        item.highestBidderId = data.highestBidderId;
      }
    } catch (error) {
      console.error("Error placing bid:", error);
      alert("Failed to place bid. Please try again.");
      setIsPlacingBid(false);
    }
  };

  const nextImage = () => setCurrentImageIndex((prev) => (prev + 1) % jewelleryImages.length);
  const prevImage = () => setCurrentImageIndex((prev) => (prev - 1 + jewelleryImages.length) % jewelleryImages.length);

  const attributePills: { label: string; icon: React.ReactNode; tone?: 'gold' | 'neutral' | 'purple' | 'amber' }[] = [];
  if (item.hallmark) attributePills.push({ label: item.hallmark, icon: <Stamp size={11} />, tone: 'gold' });
  if (item.metalType) attributePills.push({ label: item.metalType, icon: null, tone: 'neutral' });
  if (item.gemstone) attributePills.push({ label: item.gemstone, icon: <Diamond size={11} />, tone: 'purple' });
  if (item.caratWeight) attributePills.push({ label: `${item.caratWeight}ct`, icon: <Weight size={11} />, tone: 'neutral' });
  if (item.isOneOfOne) attributePills.push({ label: '1 of 1', icon: <Award size={11} />, tone: 'amber' });

  const pillStyle = (tone?: string) => {
    if (tone === 'gold') return { background: 'rgba(166,129,44,0.08)', border: `1px solid rgba(166,129,44,0.35)`, color: GOLD };
    if (tone === 'purple') return { background: '#F6F1FB', border: '1px solid #DCCBF0', color: '#6B3FA0' };
    if (tone === 'amber') return { background: '#FCF4E4', border: '1px solid #E9C77E', color: '#8A6314' };
    return { background: SURFACE, border: `1px solid ${LINE}`, color: INK };
  };

  return (
    <div className="fixed inset-0 z-[1000] flex flex-col lg:flex-row" style={{ background: BG, color: INK, fontFamily: SANS }}>
      <style>{`${FONT_IMPORT}`}</style>

      {/* Back Button */}
      <button
        onClick={onClose}
        className="absolute top-4 left-4 z-[60] p-2 transition-colors"
        style={{ background: SURFACE, border: `1px solid ${LINE}`, color: INK, borderRadius: R_PILL }}
        onMouseEnter={(e) => (e.currentTarget.style.background = SURFACE_ALT)}
        onMouseLeave={(e) => (e.currentTarget.style.background = SURFACE)}
      >
        <ArrowLeft size={18} />
      </button>

      {/* LEFT: IMAGE — matches .detail-image treatment (flat ivory field, thin border) */}
      <div
        className="w-full lg:w-[55%] h-[40vh] lg:h-full shrink-0 relative border-b lg:border-b-0 lg:border-r"
        style={{ background: SURFACE_ALT, borderColor: LINE }}
      >
        <div className="relative w-full h-full overflow-hidden">
          <img
            src={jewelleryImages[currentImageIndex] || 'https://placehold.co/800x800/A6812C/FDFCFA?text=Jewellery'}
            className="w-full h-full object-contain"
            alt={item.itemName || item.name}
          />
          {isSold && (
            <div className="absolute inset-0 flex items-center justify-center" style={{ background: 'rgba(23,23,26,0.72)' }}>
              <span
                className="px-6 py-2 rotate-[-15deg]"
                style={{ color: '#FDFCFA', border: '1px solid rgba(253,252,250,0.6)', fontFamily: SERIF, fontSize: 24, letterSpacing: 4 }}
              >
                SOLD
              </span>
            </div>
          )}

          {/* Badges — auction badge uses the exact .auction-badge treatment */}
          <div className="absolute top-4 left-4 flex flex-col gap-2 items-start">
            {item.authenticationBadge && <WaxSeal size={32} className="shadow-md" />}
            {item.certificate?.isCertified && (
              <span
                style={{
                  display: 'flex', alignItems: 'center', gap: 5,
                  fontFamily: MONO, fontSize: 11, padding: '5px 10px', borderRadius: R_PILL,
                  background: SURFACE, border: `1px solid ${GOLD}`, color: GOLD,
                }}
              >
                <FileCheck size={11} /> Certified
              </span>
            )}
            {isAuction && isAuctionActive && (
              <span
                style={{
                  display: 'flex', alignItems: 'center', gap: 5,
                  fontFamily: MONO, fontSize: 11, padding: '6px 10px', borderRadius: R_PILL,
                  background: BURGUNDY, color: '#F4E9C9',
                }}
              >
                <Gavel size={11} /> Live Auction
              </span>
            )}
          </div>

          {jewelleryImages.length > 1 && (
            <>
              <button
                onClick={prevImage}
                className="absolute left-3 top-1/2 -translate-y-1/2 p-2 transition-all"
                style={{ background: SURFACE, border: `1px solid ${LINE}`, color: INK, borderRadius: R_PILL }}
              >
                <ChevronLeft size={18} />
              </button>
              <button
                onClick={nextImage}
                className="absolute right-3 top-1/2 -translate-y-1/2 p-2 transition-all"
                style={{ background: SURFACE, border: `1px solid ${LINE}`, color: INK, borderRadius: R_PILL }}
              >
                <ChevronRight size={18} />
              </button>
              <div className="absolute bottom-4 left-0 right-0 flex justify-center gap-2">
                {jewelleryImages.map((_: any, idx: number) => (
                  <div
                    key={idx}
                    style={{
                      height: 3, borderRadius: R_PILL, transition: 'all 0.2s',
                      width: idx === currentImageIndex ? 22 : 6,
                      background: idx === currentImageIndex ? GOLD : 'rgba(23,23,26,0.2)',
                    }}
                  />
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      {/* RIGHT: DETAILS */}
      <div className="w-full lg:w-[45%] flex flex-col overflow-y-auto h-[60vh] lg:h-full" style={{ background: BG }}>
        {/* Header — h1 in Cormorant Garamond, price in JetBrains Mono, exactly .detail-info h1 / .detail-price */}
        <div className="p-6 border-b shrink-0" style={{ borderColor: LINE }}>
          <div className="flex justify-between items-start gap-3">
            <div className="flex-1 min-w-0">
              <h1
                style={{
                  fontFamily: SERIF, fontWeight: 500, color: INK,
                  fontSize: 'clamp(24px, 4vw, 32px)', lineHeight: 1.15, margin: '0 0 8px',
                }}
                className="truncate"
              >
                {item.itemName || item.name}
              </h1>
              <div className="flex items-center gap-2 flex-wrap mb-3" style={{ fontSize: 13.5, color: MUTED }}>
                {item.condition && <ConditionTag text={item.condition} />}
                {isSold && <span style={{ fontFamily: MONO, fontSize: 11, color: BURGUNDY }}>· SOLD</span>}
              </div>

              {!isAuction && (
                <div style={{ fontFamily: MONO, fontSize: 'clamp(20px, 4vw, 26px)', color: INK }}>
                  R{displayPrice?.toFixed(2)}
                </div>
              )}
              {isAuction && (
                <div>
                  <div style={{ fontFamily: MONO, fontSize: 11.5, color: MUTED, textTransform: 'uppercase', letterSpacing: 0.5 }}>Current bid</div>
                  <div style={{ fontFamily: MONO, fontSize: 'clamp(22px, 5vw, 28px)', color: INK, margin: '4px 0' }}>R{displayPrice?.toFixed(2)}</div>
                  <div style={{ fontFamily: MONO, fontSize: 12, color: MUTED }}>{item.numberOfBids || 0} bids</div>
                </div>
              )}
            </div>
            <div className="flex gap-2 shrink-0">
              <button
                onClick={() => onToggleWishlist?.(item.id, 'product')}
                className="p-2 transition-colors"
                style={{ background: SURFACE, border: `1px solid ${LINE}`, borderRadius: R_BTN }}
              >
                <Heart size={17} fill={isWishlisted ? "currentColor" : "none"} style={{ color: isWishlisted ? GOLD : MUTED }} />
              </button>
              <button
                onClick={() => setShowShareMenu(true)}
                className="p-2 transition-colors"
                style={{ background: SURFACE, border: `1px solid ${LINE}`, borderRadius: R_BTN, color: INK }}
              >
                <Share2 size={17} />
              </button>
            </div>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-6" style={{ display: 'flex', flexDirection: 'column', gap: 22 }}>

          {/* Attribute pills — rounded like .cat-pill, not neon dark-mode tints */}
          {attributePills.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {attributePills.map((p, i) => (
                <span
                  key={i}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: 6,
                    padding: '8px 14px', borderRadius: R_PILL, fontSize: 12.5,
                    ...pillStyle(p.tone),
                  }}
                >
                  {p.icon}{p.label}
                </span>
              ))}
            </div>
          )}

          {/* Auction timer strip */}
          {isAuction && isAuctionActive && item.auctionEndDate && (
            <div
              className="flex items-center justify-between"
              style={{ border: `1px solid ${LINE}`, borderRadius: R_PANEL, padding: 14 }}
            >
              <div className="flex items-center gap-2" style={{ color: MUTED, fontSize: 13 }}>
                <Timer size={16} style={{ color: GOLD }} />
                Auction ends
              </div>
              <AuctionTimer endsAt={item.auctionEndDate} />
            </div>
          )}

          {/* Jeweller strip — exact .jeweller-strip: seal + name/location, gold hover wash, "View profile →" */}
          <div
            onClick={handleViewProfile}
            className="flex items-center gap-3 cursor-pointer transition-all"
            style={{ border: `1px solid ${LINE}`, borderRadius: R_PANEL, padding: 14 }}
            onMouseEnter={(e) => { e.currentTarget.style.borderColor = GOLD; e.currentTarget.style.background = '#FDFAF2'; }}
            onMouseLeave={(e) => { e.currentTarget.style.borderColor = LINE; e.currentTarget.style.background = 'transparent'; }}
          >
            <div style={{ width: 40, height: 40, borderRadius: '50%', overflow: 'hidden', flexShrink: 0, background: SURFACE_ALT, border: `1px solid ${LINE}`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              {item.userAvatar || item.sellerAvatar ? (
                <img src={item.userAvatar || item.sellerAvatar} className="w-full h-full object-cover" alt={sellerName} />
              ) : (
                <Gem size={16} style={{ color: MUTED }} />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <div style={{ fontFamily: SERIF, fontSize: 16.5, color: INK }}>{sellerName}</div>
              <div style={{ fontSize: 12.5, color: MUTED }}>Independently verified jeweller</div>
            </div>
            <span style={{ fontSize: 12, color: GOLD, whiteSpace: 'nowrap' }}>View profile →</span>
          </div>

          {/* Primary actions */}
          {!isSold && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <div className="flex gap-3">
                <PrimaryButton onClick={handleAddToCart} disabled={isInCart} style={{ flex: 1 }}>
                  {isInCart ? <><CheckCircle size={15} /> In cart</> : <><ShoppingBag size={15} /> Add to cart</>}
                </PrimaryButton>
                <OutlineButton onClick={handleBuyNow} style={{ flex: 1 }}>
                  <CreditCard size={15} /> Buy now
                </OutlineButton>
              </div>

              <div className="relative" style={{ margin: '4px 0' }}>
                <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center' }}>
                  <div style={{ width: '100%', borderTop: `1px solid ${LINE}` }} />
                </div>
                <div style={{ position: 'relative', display: 'flex', justifyContent: 'center' }}>
                  <span style={{ padding: '0 12px', background: BG, color: MUTED, fontSize: 11, fontFamily: MONO }}>or</span>
                </div>
              </div>

              {isAuction && isAuctionActive ? (
                <div className="flex gap-2">
                  <div style={{ flex: 1, display: 'flex', alignItems: 'center', border: `1px solid ${LINE}`, borderRadius: R_BTN, padding: '0 12px' }}>
                    <span style={{ color: MUTED, marginRight: 4, fontFamily: MONO }}>R</span>
                    <input
                      type="number"
                      value={bidAmount}
                      onChange={(e) => setBidAmount(e.target.value)}
                      placeholder={nextBidAmount.toFixed(2)}
                      style={{ border: 'none', padding: '12px 0', fontFamily: MONO, fontSize: 15, width: '100%', outline: 'none', background: 'transparent', color: INK }}
                    />
                  </div>
                  <PrimaryButton onClick={handlePlaceBid} disabled={isPlacingBid || !bidAmount}>
                    {isPlacingBid ? <Loader2 size={14} className="animate-spin" /> : <Gavel size={14} />}
                    {isPlacingBid ? 'Placing…' : 'Place bid'}
                  </PrimaryButton>
                </div>
              ) : (
                <div className="flex gap-3">
                  <OutlineButton onClick={() => setIsMakingOffer(true)} colorOverride={GOLD} style={{ flex: 1 }}>
                    <Tag size={14} /> Make offer
                  </OutlineButton>
                  <OutlineButton onClick={handleSendMessage} colorOverride={MUTED} style={{ flex: 1 }}>
                    <MessageSquare size={14} /> Message
                  </OutlineButton>
                </div>
              )}

              {isMakingOffer && !isAuction && (
                <div style={{ background: 'rgba(166,129,44,0.06)', border: `1px solid rgba(166,129,44,0.3)`, borderRadius: R_PANEL, padding: 16 }}>
                  <p style={{ fontFamily: MONO, fontSize: 11.5, color: GOLD, marginBottom: 10 }}>
                    Your offer (current R{displayPrice?.toFixed(2)})
                  </p>
                  <div className="flex items-center gap-2">
                    <div style={{ flex: 1, position: 'relative' }}>
                      <span style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: GOLD, fontFamily: MONO }}>R</span>
                      <input
                        type="number"
                        autoFocus
                        value={offerAmount}
                        onChange={(e) => setOfferAmount(e.target.value)}
                        placeholder="Amount"
                        style={{ width: '100%', borderRadius: R_BTN, padding: '10px 12px 10px 28px', fontFamily: MONO, fontSize: 14, outline: 'none', background: SURFACE, border: `1px solid rgba(166,129,44,0.3)`, color: INK }}
                      />
                    </div>
                    <PrimaryButton onClick={handleMakeOffer} disabled={!offerAmount || isSendingAction} style={{ padding: '11px 18px' }}>
                      {isSendingAction ? <Loader2 size={14} className="animate-spin" /> : 'Send'}
                    </PrimaryButton>
                    <button
                      onClick={() => setIsMakingOffer(false)}
                      style={{ padding: 10, background: SURFACE, border: `1px solid ${LINE}`, color: MUTED, borderRadius: R_BTN }}
                    >
                      <X size={14} />
                    </button>
                  </div>
                </div>
              )}

              {!isMakingOffer && !isAuction && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, border: `1px solid ${LINE}`, borderRadius: R_PANEL, padding: 10 }}>
                  <input
                    type="text"
                    value={quickMessage}
                    onChange={(e) => setQuickMessage(e.target.value)}
                    placeholder="Ask jeweller…"
                    style={{ flex: 1, background: 'transparent', border: 'none', outline: 'none', fontSize: 13, color: INK }}
                  />
                  {showSuccess ? (
                    <CheckCircle size={16} style={{ color: '#2E5E3E', flexShrink: 0 }} />
                  ) : (
                    <button
                      onClick={handleQuickAsk}
                      disabled={isSendingAction || !quickMessage.trim()}
                      style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', background: INK, color: '#FDFCFA', fontSize: 12.5, borderRadius: R_BTN, flexShrink: 0 }}
                    >
                      {isSendingAction ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />}
                      Send
                    </button>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Tabs — exact .tabs / .tab treatment: Details / Description / Jeweller */}
          <div style={{ marginTop: 8 }}>
            <div className="flex gap-6" style={{ borderBottom: `1px solid ${LINE}`, marginBottom: 20 }}>
              {(['details', 'description', 'jeweller'] as const).map((t) => (
                <button
                  key={t}
                  onClick={() => setTab(t)}
                  style={{
                    background: 'none', border: 'none', padding: '10px 0', fontSize: 14, cursor: 'pointer',
                    color: tab === t ? INK : MUTED,
                    borderBottom: `2px solid ${tab === t ? GOLD : 'transparent'}`,
                    textTransform: 'capitalize',
                  }}
                >
                  {t}
                </button>
              ))}
            </div>

            {tab === 'details' && (
              specs.length > 0 ? (
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <tbody>
                    {specs.map(([k, v]) => (
                      <tr key={k}>
                        <td style={{ padding: '11px 0', borderBottom: `1px solid ${LINE}`, fontSize: 13, color: MUTED, width: 150 }}>{k}</td>
                        <td style={{ padding: '11px 0', borderBottom: `1px solid ${LINE}`, fontSize: 13, fontFamily: MONO, color: INK }}>{v}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <p style={{ color: MUTED, fontSize: 13.5 }}>No specifications listed for this piece.</p>
              )
            )}

            {tab === 'description' && (
              <p style={{ lineHeight: 1.75, fontSize: 14, color: INK }}>
                {item.description || 'No description provided for this piece yet.'}
              </p>
            )}

            {tab === 'jeweller' && (
              <div className="flex gap-4 items-start flex-wrap">
                <WaxSeal size={52} />
                <div style={{ flex: 1, minWidth: 200 }}>
                  <div style={{ fontFamily: SERIF, fontSize: 19, color: INK, marginBottom: 4 }}>{sellerName}</div>
                  <p style={{ fontSize: 12.5, color: MUTED, margin: 0 }}>Independently verified jeweller</p>
                  <p style={{ fontSize: 13, lineHeight: 1.65, color: INK, marginTop: 12 }}>
                    Identity, workshop or sourcing documentation, and a sample of past work were reviewed
                    before this profile was approved to sell on Scoop &amp; Stone.
                  </p>
                  <div style={{ marginTop: 14 }}>
                    <OutlineButton onClick={handleViewProfile} style={{ width: 'fit-content', padding: '10px 18px', fontSize: 13 }}>
                      View full profile &amp; all listings →
                    </OutlineButton>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div style={{ fontFamily: MONO, fontSize: 11, color: MUTED, marginTop: 4 }}>
            Reference {item.id}
          </div>
        </div>
      </div>

      {/* Share Menu Modal */}
      {showShareMenu && (
        <div
          className="fixed inset-0 z-[1200] flex items-center justify-center p-6"
          style={{ background: 'rgba(23,23,26,0.55)' }}
          onClick={() => setShowShareMenu(false)}
        >
          <div
            className="w-full max-w-xs text-center"
            style={{ background: SURFACE, border: `1px solid ${LINE}`, borderRadius: R_PANEL, padding: 26, boxShadow: '0 24px 48px rgba(0,0,0,0.15)' }}
            onClick={e => e.stopPropagation()}
          >
            <div style={{ width: 52, height: 52, borderRadius: R_PANEL, background: SURFACE_ALT, border: `1px solid ${LINE}`, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
              <Share2 style={{ color: GOLD }} size={22} />
            </div>
            <h3 style={{ fontFamily: SERIF, fontSize: 20, color: INK, margin: 0 }}>Share this piece</h3>
            <p style={{ fontSize: 12.5, color: MUTED, margin: '6px 0 22px' }}>
              "{item.itemName || item.name}"
            </p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <PrimaryButton onClick={handleNativeShare}>
                <Users size={14} /> Share
              </PrimaryButton>
              <OutlineButton onClick={handleCopyLink}>
                <LinkIcon size={14} /> Copy link
              </OutlineButton>
            </div>

            <button
              onClick={() => setShowShareMenu(false)}
              style={{ marginTop: 16, background: 'none', border: 'none', color: MUTED, fontSize: 12.5, cursor: 'pointer' }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default JewelleryProductDetailModal;